import axios from 'axios';
import { getUserData } from './storageService';

// Use same base URL pattern as other services
const BASE_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com';
const API_URL = BASE_URL.replace(/\/api$/, '');

// Create axios instance with auth header
const getAuthHeaders = () => {
  const user = getUserData();
  return {
    Authorization: `Bearer ${user?.userId || ''}`
  };
};

const api = axios.create({
  baseURL: `${API_URL}/api/sheets`,
});

// Add auth header to all requests
api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

/**
 * Content Service - API calls for Google Sheets content management
 */
const contentService = {
  /**
   * Get all spreadsheet configurations
   */
  getConfigs: async () => {
    const response = await api.get('/configs');
    return response.data;
  },

  /**
   * Get spreadsheet configuration (default or by id)
   */
  getConfig: async (configId = null) => {
    const url = configId ? `/config?configId=${configId}` : '/config';
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Add new spreadsheet configuration
   */
  saveConfig: async (config) => {
    const response = await api.post('/config', config);
    return response.data;
  },

  /**
   * Delete spreadsheet configuration
   */
  deleteConfig: async (configId) => {
    const response = await api.delete(`/config/${configId}`);
    return response.data;
  },

  /**
   * Set default spreadsheet
   */
  setDefaultConfig: async (configId) => {
    const response = await api.put(`/config/${configId}/default`);
    return response.data;
  },

  /**
   * Validate spreadsheet access
   */
  validateSpreadsheet: async (spreadsheetId) => {
    const response = await api.post('/validate', { spreadsheetId });
    return response.data;
  },

  /**
   * Get all content data
   */
  getContentData: async (filters = {}, configId = null) => {
    const params = new URLSearchParams();
    if (configId) params.append('configId', configId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    
    const response = await api.get(`/data?${params.toString()}`);
    return response.data;
  },

  /**
   * Get single row
   */
  getRow: async (rowIndex) => {
    const response = await api.get(`/row/${rowIndex}`);
    return response.data;
  },

  /**
   * Update content row
   */
  updateContent: async (rowIndex, data) => {
    const response = await api.put(`/row/${rowIndex}`, data);
    return response.data;
  },

  /**
   * Add new content row
   */
  addContent: async (data) => {
    const response = await api.post('/row', data);
    return response.data;
  },

  /**
   * Delete content row
   */
  deleteContent: async (rowIndex) => {
    const response = await api.delete(`/row/${rowIndex}`);
    return response.data;
  },

  /**
   * Get statistics
   */
  getStats: async () => {
    const response = await api.get('/stats');
    return response.data;
  },

  /**
   * Get available channels
   */
  getChannels: async () => {
    const response = await api.get('/channels');
    return response.data;
  },

  /**
   * Parse spreadsheet ID from URL
   */
  parseSpreadsheetId: (url) => {
    // Handle full URL: https://docs.google.com/spreadsheets/d/SPREADSHEET_ID/edit
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      return match[1];
    }
    // If it's already just an ID, return as-is
    if (/^[a-zA-Z0-9-_]+$/.test(url)) {
      return url;
    }
    return null;
  },
};

export default contentService;
